import { useEffect, useState } from 'react';
import {
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Switch,
  Text,
} from '@mantine/core';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

interface CookiePreferences {
  necessary: boolean;
  marketing: boolean;
  analytics: boolean;
}

export const CookieConsentManager = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    marketing: false,
    analytics: false,
  });

  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const lastInteraction = localStorage.getItem('cookieConsentDate');

    if (!savedPreferences || !lastInteraction) {
      setShowBanner(true);
      return;
    }

    // Check if 90 days have passed
    const lastDate = new Date(lastInteraction);
    const now = new Date();
    const daysDiff = (now.getTime() - lastDate.getTime()) / (1000 * 3600 * 24);

    if (daysDiff > 90) {
      setShowBanner(true);
      return;
    }

    // Apply saved preferences
    const parsed = JSON.parse(savedPreferences);
    setPreferences(parsed);
    updateGoogleAnalytics(parsed.analytics);
  }, []);

  const updateGoogleAnalytics = (enabled: boolean) => {
    if (typeof window.gtag !== 'function') return;

    if (enabled) {
      // Enable GA tracking
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    } else {
      // Disable GA tracking
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  };

  const savePreferences = (newPreferences: CookiePreferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowModal(false);
    updateGoogleAnalytics(newPreferences.analytics);
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      marketing: true,
      analytics: true,
    });
  };

  const handleBlockNonRequired = () => {
    savePreferences({
      necessary: true,
      marketing: false,
      analytics: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner && !showModal) {
    return null;
  }

  return (
    <>
      {showBanner && (
        <Paper
          shadow="md"
          pos="fixed"
          bottom={0}
          left={0}
          right={0}
          p="md"
          style={{ zIndex: 1000 }}
          withBorder
        >
          <Group justify="space-between" gap="md" wrap="wrap">
            <Text size="sm" style={{ flex: 1 }}>
              We use cookies to enhance your experience. By continuing to visit
              this site you agree to our use of cookies.
            </Text>
            <Group gap="sm" wrap="wrap">
              <Button variant="filled" onClick={handleAcceptAll}>
                Accept All
              </Button>
              <Button variant="light" onClick={handleBlockNonRequired}>
                Block Non-Required
              </Button>
              <Button variant="subtle" onClick={() => setShowModal(true)}>
                Manage Cookies
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <Modal
        opened={showModal}
        onClose={() => setShowModal(false)}
        title="Cookie Preferences"
        size="md"
      >
        <Stack>
          <div>
            <Switch
              checked={preferences.necessary}
              disabled
              label="Necessary Cookies"
              description="Required for the website to function properly"
            />
          </div>
          <div>
            <Switch
              checked={preferences.marketing}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  marketing: e.currentTarget.checked,
                })
              }
              label="Marketing Cookies"
              description="Used for marketing and personalization"
            />
          </div>
          <div>
            <Switch
              checked={preferences.analytics}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  analytics: e.currentTarget.checked,
                })
              }
              label="Analytics Cookies"
              description="Help us understand how visitors interact with our website"
            />
          </div>
          <Button onClick={handleSavePreferences}>Save Preferences</Button>
        </Stack>
      </Modal>
    </>
  );
};
